/**
 * Desktop footer hold. Scrolling down stops where the content fills the view, so the footer
 * never slides in on that scroll. The page then stays caught until the reader keeps pushing:
 * wheel, trackpad or ArrowDown fill a pressure gauge that drains when they stop, and only a full
 * gauge lets the footer up. The gesture that got caught adds nothing, and no single notch or
 * fling can blast through. The old gsap pin gave a fixed 500px of hold, lost when the pin became
 * CSS sticky.
 *
 * The pressure constants are the investor deck's section gate, tuned there by feel
 * (UI Concepts/section-gate/section-gate-handoff.md §3). From rest: catch, then 3 wheel notches.
 */
export interface FooterHoldOptions {
  /** The footer to hold back. It can mount after the dashboard, so it is looked up each time. */
  footer: () => Element | null;
  /** Narrowest viewport that holds; matches the sticky hero/content pin. */
  minWidth?: number;
  /** Wheel events closer together than this are one gesture, including trackpad momentum. */
  gestureGapMs?: number;
  win?: Window;
}

const RELEASE_AT = 85; // pressure that lets the footer through
const WHEEL_DIVISOR = 2.4;
const WHEEL_CAP = 90; // one wheel event adds at most 90 / 2.4 = 37.5
const ARROW_PUSH = 34;
const IDLE_MS = 110; // stillness before the gauge drains
const DRAIN_PER_MS = (RELEASE_AT * 0.017 * 60) / 1000; // the deck drains 1.445 per 60 Hz frame
const PIN_SLOP = 0.5; // sub-pixel scroll positions; a whole pixel would already show the footer
// Scrolls this soon after wheel or keyboard input are the reader's scrolling; any other scroll
// (scrollbar drag, find-in-page, restore) is never fought.
const INPUT_WINDOW_MS = 700;

export function holdFooterBetweenGestures(options: FooterHoldOptions): () => void {
  const win = options.win ?? window;
  const doc = win.document;
  const gestureGap = options.gestureGapMs ?? 200;
  const media = win.matchMedia(`(min-width: ${options.minWidth ?? 1199}px)`);

  let held = false; // caught at the content end
  let passing = false; // released: free into the footer until the page is back above the stop
  let swallowing = false; // the wheel gesture that got caught; it adds no pressure
  let pressure = 0;
  let lastPush = 0;
  let lastDrainAt = 0;
  let draining = 0;
  let lastInput = -Infinity;
  let lastWheel = -Infinity;
  let pageScrolls = false; // this wheel gesture scrolls the page rather than an inner list
  let target: number | null = null; // where this wheel gesture is taking the page
  let lastY = win.scrollY;

  // The scroll position at which the footer's top edge reaches the bottom of the viewport.
  const boundary = (): number | null => {
    const footer = options.footer();
    if (!footer) return null;
    const limit = Math.floor(footer.getBoundingClientRect().top + win.scrollY - win.innerHeight);
    return limit >= 0 ? limit : null;
  };

  // Bootstrap sets `scroll-behavior: smooth` on the root; a smooth stop would glide past.
  const stopAt = (top: number) => {
    win.scrollTo({ top, behavior: 'instant' });
    lastY = top;
  };

  const pageLocked = () => [doc.documentElement, doc.body]
    .some(el => win.getComputedStyle(el).overflowY === 'hidden');

  const catchAt = (limit: number, fromWheel: boolean) => {
    held = true;
    passing = false;
    swallowing = fromWheel;
    pressure = 0;
    target = null;
    if (Math.abs(win.scrollY - limit) > 0) stopAt(limit);
  };

  const letGo = (intoFooter: boolean) => {
    held = false;
    passing = intoFooter;
    pressure = 0;
    cancelDrain();
  };

  const push = (amount: number) => {
    pressure += amount;
    lastPush = win.performance.now();
    if (pressure >= RELEASE_AT) {
      letGo(true);
    } else if (!draining) {
      lastDrainAt = lastPush;
      draining = win.requestAnimationFrame(drain);
    }
  };

  // Drains by elapsed time, so the gauge empties at the same pace at any refresh rate.
  const drain = (now: number) => {
    draining = 0;
    if (!held || pressure <= 0) return;
    const elapsed = Math.min(now - lastDrainAt, 50);
    lastDrainAt = now;
    if (now - lastPush > IDLE_MS) pressure = Math.max(0, pressure - DRAIN_PER_MS * elapsed);
    if (pressure > 0) draining = win.requestAnimationFrame(drain);
  };

  const cancelDrain = () => {
    if (draining) win.cancelAnimationFrame(draining);
    draining = 0;
  };

  const onWheel = (event: WheelEvent) => {
    if (event.timeStamp - lastWheel > gestureGap) {
      swallowing = false;
      target = null;
      // The browser keeps a gesture on the scroller it started on, like this does.
      pageScrolls = !innerScrollerTakes(event, win);
    }
    lastWheel = event.timeStamp;
    if (!pageScrolls || event.ctrlKey || pageLocked()) return;
    lastInput = win.performance.now();
    const dy = wheelPixels(event, win);
    if (Math.abs(dy) < 1) return;
    if (dy < 0) {
      if (held) letGo(false);
      target = null;
      return;
    }
    if (held) {
      event.preventDefault();
      if (!swallowing) push(Math.min(dy, WHEEL_CAP) / WHEEL_DIVISOR);
      return;
    }
    if (passing) return;
    const limit = boundary();
    const y = win.scrollY;
    if (limit === null || y > limit + 1) return; // the footer is already showing
    if (target === null && y >= limit - 1) {
      // At the content end without having been caught (page opened or dragged here):
      // this is the first scroll.
      event.preventDefault();
      catchAt(limit, true);
      return;
    }
    // Wheel smooth-scrolling accumulates, so aim from wherever the gesture was already going.
    target = Math.max(target ?? y, y) + dy;
    if (target >= limit) {
      event.preventDefault();
      catchAt(limit, true);
    }
  };

  const onScroll = () => {
    const y = win.scrollY;
    const limit = boundary();
    if (limit === null) {
      lastY = y;
      return;
    }
    const reading = win.performance.now() - lastInput < INPUT_WINDOW_MS;
    if (y < limit - 1) {
      // Back above the stop: the next scroll down is caught again.
      if (held || passing) letGo(false);
    } else if (held) {
      if (y - limit <= PIN_SLOP) {
        // resting on the stop
      } else if (reading) {
        stopAt(limit);
      } else {
        letGo(true);
      }
    } else if (!passing && lastY <= limit + 1 && y > limit + 1) {
      // Crossed the stop without a wheel catch: keyboard scrolling or a smooth scroll in flight.
      if (reading) catchAt(limit, win.performance.now() - lastWheel <= gestureGap);
      else passing = true;
    }
    lastY = win.scrollY;
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.defaultPrevented || isEditable(event.target) || !SCROLL_KEYS.has(event.key)) return;
    lastInput = win.performance.now();
    if (!held) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      push(ARROW_PUSH);
    } else if (event.key === 'PageDown' || event.key === 'End' || (event.key === ' ' && !event.shiftKey)) {
      letGo(true); // a page-sized step is deliberate; let the browser carry on into the footer
    }
  };

  let listening = false;
  const sync = () => {
    // A non-passive wheel listener makes the browser wait on script before scrolling; only
    // pay for it where the hold applies.
    if (media.matches === listening) return;
    listening = media.matches;
    if (listening) {
      lastY = win.scrollY;
      win.addEventListener('wheel', onWheel, { passive: false });
      win.addEventListener('scroll', onScroll, { passive: true });
      win.addEventListener('keydown', onKeydown);
    } else {
      removeListeners();
      letGo(false);
      target = null;
    }
  };
  const removeListeners = () => {
    win.removeEventListener('wheel', onWheel);
    win.removeEventListener('scroll', onScroll);
    win.removeEventListener('keydown', onKeydown);
  };
  sync();
  media.addEventListener('change', sync);

  return () => {
    media.removeEventListener('change', sync);
    removeListeners();
    cancelDrain();
  };
}

const SCROLL_KEYS = new Set(['ArrowDown', 'PageDown', 'End', ' ']);

function wheelPixels(event: WheelEvent, win: Window): number {
  if (event.deltaMode === 1) return event.deltaY * 16; // lines
  if (event.deltaMode === 2) return event.deltaY * win.innerHeight; // pages
  return event.deltaY;
}

function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
}

/** Whether a list or panel under the pointer will scroll instead of the page. */
function innerScrollerTakes(event: WheelEvent, win: Window): boolean {
  const doc = win.document;
  let el = event.target instanceof Element ? event.target : null;
  for (; el && el !== doc.body && el !== doc.documentElement; el = el.parentElement) {
    if (el.scrollHeight <= el.clientHeight + 1) continue;
    const overflowY = win.getComputedStyle(el).overflowY;
    if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') continue;
    const canMove = event.deltaY > 0
      ? el.scrollTop + el.clientHeight < el.scrollHeight - 1
      : el.scrollTop > 0;
    if (canMove) return true;
  }
  return false;
}
